/* eslint-disable no-void */

import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';

import type { PartnerAccountCreationLinkResponse, PartnerAccountCreationResponse } from 'vopay';
import vopayApi from '../../services/api.config';
import { useDataFetchIndicator } from '..';
import { buildRequestParams as buildBillingParams } from './usePostCustomPackage';
import { NewBillingPackageData } from '@/components/BillingPackages/types';

type HookProps = {
  api: vp.Api;
};

export type PartnerAccountRequest = {
  accountName: string;
  legalBusinessName: string;
  fullLegalName: string;
  businessType: string;
  industry: string;
  industrySubCategory: string;
  websiteURL: string;
  businessPhone: string;
  accountingSoftwareIntegrationEnabled: boolean;
  hubspotCompanyID?: string;
  hubspotDealID?: string;
  emailAddress: string;
  businessName: string;
  country: string;
  address: string;
  unit?: string;
  city: string;
  province: string;
  postalCode: string;
  isClientAccountsEnabled: boolean;
  packageType?: string;
  legalAgreementNotes?: string;
  billingPackageData?: NewBillingPackageData;
};

export type PartnerAccountResponse = {
  success: boolean;
  errorMessage: string;
  accountId: string;
  apiSharedSecret: string;
  apiKey: string;
  message: string;
  onboardingLink: string;
};

type ReturnTuple = [PartnerAccountResponse | undefined, (requestParams: PartnerAccountRequest) => void, () => void];

function usePostPartnerAccount({ api }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<PartnerAccountRequest>();
  const [response, setResponse] = useState<PartnerAccountResponse>();
  const [shouldSendRequest, setSendRequest] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  useEffect(() => {
    const sendRequest = async (requestParams: PartnerAccountRequest) => {
      setDataFetchingInfo(true, 'sending account data', 'fullScreen');

      const apiRes = await api.postPartnerAccount(buildParams(requestParams), source);

      const res = {
        success: apiRes.Success,
        errorMessage: apiRes.ErrorMessage,
        accountId: hasAccountId(apiRes) ? apiRes.AccountID : '',
        apiSharedSecret: hasApiSharedSecret(apiRes) ? apiRes.APISharedSecret : '',
        apiKey: hasApiKey(apiRes) ? apiRes.APIKey : '',
        message: hasMessage(apiRes) ? apiRes.Message : '',
        onboardingLink: hasLink(apiRes) ? apiRes.Link : '',
      };

      setResponse(res);
      setSendRequest(false);
      setDataFetchingInfo(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams?: PartnerAccountRequest) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setResponse(undefined);
      setSendRequest(false);
    },
  ];

  function buildParams(requestParams: PartnerAccountRequest): vp.PartnerAccountRequest {
    const billingParams = requestParams.billingPackageData ? buildBillingParams(requestParams.billingPackageData) : {};
    // map business requests
    return vp.removeEmptyProperty({
      ...billingParams,
      Name: requestParams.accountName,
      LegalBusinessName: requestParams.legalBusinessName,
      EmailAddress: requestParams.emailAddress,
      BusinessName: requestParams.businessName,
      FullLegalName: requestParams.fullLegalName,
      Country: requestParams.country,
      ClientAccountsEnabled: requestParams.isClientAccountsEnabled,
      BillingPackageID: requestParams.packageType,
      AccountingSoftwareIntegrationEnabled: requestParams.accountingSoftwareIntegrationEnabled,
      OrganizationalType: requestParams.businessType,
      BusinessTypeID: requestParams.industry,
      BusinessTypeCategoryID: requestParams.industrySubCategory,
      HubspotCompany_ID: requestParams.hubspotCompanyID,
      HubspotDeal_ID: requestParams.hubspotDealID,
      Address1: requestParams.address,
      Address2: requestParams.unit,
      City: requestParams.city,
      Province: requestParams.province,
      PostalCode: requestParams.postalCode,
      WebsiteURL: requestParams.websiteURL ? vp.sanatizeURL(requestParams.websiteURL) : requestParams.websiteURL,
      BusinessPhone: requestParams.businessPhone,
      LegalAgreementNotes: requestParams.legalAgreementNotes,
      SendWelcomeEmail: true,
    });
  }

  function hasLink(res: unknown): res is PartnerAccountCreationLinkResponse {
    return Object.prototype.hasOwnProperty.call(res, 'Link');
  }

  function hasAccountId(res: unknown): res is PartnerAccountCreationResponse {
    return Object.prototype.hasOwnProperty.call(res, 'AccountID');
  }

  function hasMessage(res: unknown): res is PartnerAccountCreationResponse {
    return Object.prototype.hasOwnProperty.call(res, 'Message');
  }

  function hasApiSharedSecret(res: unknown): res is PartnerAccountCreationResponse {
    return Object.prototype.hasOwnProperty.call(res, 'APISharedSecret');
  }

  function hasApiKey(res: unknown): res is PartnerAccountCreationResponse {
    return Object.prototype.hasOwnProperty.call(res, 'APIKey');
  }
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(usePostPartnerAccount);
